import { AuthFailed } from 'app/types/auth';
import { reportError } from 'app/utils/debug_errors';

type QueryValue = string | number | boolean;
export type QueryParams = Record<string, QueryValue | ReadonlyArray<QueryValue> | null | undefined>;

// Array.isArray doesn't narrow readonly arrays
const isList = (value: QueryParams[string]): value is ReadonlyArray<QueryValue> => Array.isArray(value);

interface JsonResponse {
  data?: Array<unknown>;
}

/**
 * The apis we talk to do not agree on where the error message lives:
 *
 *   our backend   -> { message }
 *   coingecko     -> { error }  (or { error: { message } })
 *   cryptocompare -> { Err: { message } }
 *
 * Falling back to the status code keeps `message` a string in every case, which
 * matters because callers do things like `error.message.match(/rate limit/i)`.
 */
// a property of a value that may not be an object
const field = (value: unknown, key: string): unknown =>
  value != null && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined;

export const extractErrorMessage = (json: unknown, response: Response): string => {
  const asString = (value: unknown) => (typeof value === 'string' ? value : undefined);

  const candidate =
    asString(field(json, 'message')) ??
    asString(field(json, 'error')) ??
    asString(field(field(json, 'error'), 'message')) ??
    asString(field(field(json, 'Err'), 'message'));

  return candidate ?? `request failed with status ${response?.status}`;
};

export class ResponseError extends Error {
  constructor(
    public json: AuthFailed,
    public response: Response
  ) {
    super(extractErrorMessage(json, response));
    this.name = 'ResponseError';
    this.json = json;
    this.response = response;

    // this allows using instanceof to narrow the type
    // see here for more details: https://stackoverflow.com/a/41429145/410286
    Object.setPrototypeOf(this, ResponseError.prototype);
  }
}

type RequestJsonOptions = {
  body?: unknown;
  query?: QueryParams;
  headers?: Record<string, string>;
};

/**
 * ApiService contains all functions used to make requests to the backend. It also
 * contains helper functions that are used to help make web requests less verbose
 *
 * expects this.baseUrl to be set
 */
export abstract class WebRequestService<PayloadStructure = JsonResponse> {
  abstract baseUrl: string;
  defaultHeaders: Record<string, string>;
  defaultQueryParams: QueryParams;

  constructor() {
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
    this.defaultQueryParams = {};
  }

  abstract extractJsonData(json: PayloadStructure): unknown;

  // the response's shape is up to the caller: T is whatever extractJsonData pulls out of it
  getJson<T = unknown>(endpoint: string, json: QueryParams = {}, headers: Record<string, string> = {}) {
    return this.requestJson<T>('GET', endpoint, { headers, query: json });
  }

  postJson<T = unknown>(endpoint: string, json: unknown = {}, headers: Record<string, string> = {}) {
    return this.requestJson<T>('POST', endpoint, { headers, body: json });
  }

  putJson<T = unknown>(endpoint: string, json: unknown = {}, headers: Record<string, string> = {}) {
    return this.requestJson<T>('PUT', endpoint, { headers, body: json });
  }

  buildQueryString(params: QueryParams): string {
    const parts: string[] = [];
    for (const [key, value] of Object.entries(params)) {
      if (isList(value)) {
        value.forEach(v => parts.push(`${key}[]=${encodeURIComponent(v)}`));
      } else if (value !== undefined && value !== null) {
        parts.push(`${key}=${encodeURIComponent(value)}`);
      }
    }
    return parts.length ? '?' + parts.join('&') : '';
  }

  requestJson<T = unknown>(
    method: string,
    endpoint: string,
    { body = {}, query = {}, headers = {} }: RequestJsonOptions
  ): Promise<T> {
    const pathname = this.baseUrl + endpoint;
    const queryString = this.buildQueryString({ ...query, ...this.defaultQueryParams });

    // T is the caller's description of the response; it isn't checked
    return fetch(pathname + queryString, {
      method,
      credentials: 'same-origin',
      headers: { ...headers, ...this.defaultHeaders },
      body: method === 'GET' || method === 'DELETE' ? null : JSON.stringify(body),
    })
      .then(this.handleResponse.bind(this))
      .catch(err => {
        console.warn(`warning: request to ${endpoint} failed with message: ${err.message}`);
        reportError(`${method} ${pathname}: ${err.message}`);
        throw err;
      }) as Promise<T>;
  }

  handleResponse(response: Response) {
    return response
      .json()
      .catch(err => {
        throw new Error(`failed to parse response body: ${err}`);
      })
      .then(json => {
        if (!response.ok || json.status === 'error') {
          const error = new ResponseError(json, response);
          throw error;
        }
        return this.extractJsonData(json);
      });
  }
}
